import { ChangeEvent, useState } from 'react';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { FileObject, StarStoryFormData } from '@/components/blocks/StarStoryForm/StarStoryFormTabContent';
import { MediaCard } from '@/components/elements/MediaCard';
import { Icon } from '@/components/global/Icons';

interface FileInputProps extends ControllerRenderProps {
    contentType: 'Video' | 'Image';
    maxSize: string;
    acceptedFormats: string[];
    maxFileSize: number;
    acceptedFormatsText: string;
    form: UseFormReturn<StarStoryFormData>;
}

export default function FileInput({ contentType, maxSize, acceptedFormats, acceptedFormatsText, name, onChange, form, maxFileSize }: FileInputProps) {
    const { setValue, getValues } = form;
    const { images, videos } = getValues();
    const [selectedFiles, setSelectedFiles] = useState<FileObject[]>((contentType === 'Image' ? images : videos) ?? ([] as FileObject[]));
    const fieldName = contentType.toLocaleLowerCase().concat('s') as keyof StarStoryFormData;

    const existingFileErrors = selectedFiles?.some((file) => file.error);

    const [fileErrors, setFileErrors] = useState(existingFileErrors);

    const handleFileInput = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        const validatedFiles = Array.from(files || []).map((file) => {
            if (!(file instanceof File)) {
                return { file, error: true };
            }

            if (file?.size > maxFileSize || !acceptedFormats?.includes(file?.type)) {
                setFileErrors(true);
                return { file, error: true };
            }

            return { file, error: false };
        });

        onChange(validatedFiles.map((fileData) => fileData.file));

        setSelectedFiles((prevSelectedFiles) => {
            const newFiles = [...prevSelectedFiles, ...validatedFiles];
            // Need to manually set the value of 'images' so that it's an array of objects
            setValue(fieldName, newFiles);

            return newFiles;
        });
    };

    const removeSelectedFile = (indexToRemove: number) => {
        setSelectedFiles((prevSelectedFiles) => {
            const updatedFiles = [...prevSelectedFiles];
            updatedFiles.splice(indexToRemove, 1);
            setValue(fieldName, updatedFiles);
            // Check if any files have errors in the updated array
            setFileErrors(updatedFiles.some((file) => file.error));

            // Reset the value of the file input to clear the removed file, otherwise cannot reupload same file
            const fileInput = document.getElementById(name) as HTMLInputElement;
            if (fileInput) {
                fileInput.value = '';
            }

            return updatedFiles;
        });
    };

    return (
        <div className="items-center text-center mb-20 gap-10  text-primary">
            <h2>{contentType}</h2>
            <div className="border border-primary rounded shadow mb-4 items-center text-center mt-6 px-4">
                <div className="items-center justify-center mt-12">
                    <input type="file" accept={acceptedFormats?.join(',')} style={{ display: 'none' }} id={name} multiple onChange={handleFileInput} />
                    <label
                        htmlFor={name}
                        className={`custom-${name}}-button px-4 pt-2 pb-10 rounded text-2xl flex flex-col items-center cursor-pointer max-w-fit-content mx-auto font-medium`}
                    >
                        <Icon id={'upload'} className="w-12 h-12 mb-1 fill-primary group-hover:fill-white font-medium" style={{ transition: 'fill 0.3s' }} />
                        Browse Files
                    </label>
                    <p className="text-center font-medium text-xl mb-6">Upload your {contentType.toLocaleLowerCase()} submissions here.</p>
                    <p className="text-xl">{maxSize}</p>
                    <p className="text-xl mb-12">Accepted formats: {acceptedFormatsText}</p>
                    {selectedFiles?.length > 0 && (
                        <div className="flex-wrap flex justify-center w-full mb-12 sm:gap-7">
                            {selectedFiles.map((file, index) => (
                                <MediaCard
                                    key={file.file.name}
                                    file={file.file}
                                    hasWarning={!!file.error}
                                    removeFile={() => removeSelectedFile(index)}
                                    isSubmitting={form.formState.isSubmitting}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {fileErrors && <p className="text-red-600 text-lg">There was an error uploading one or more files. Please remove the failed files and try again.</p>}
        </div>
    );
}
export { FileInput };
